<template>

<header class="topbar">

	<app-wordmark :size="22" />

	<nav class="topbar-nav" aria-label="Primary">
		<router-link to="/" class="topbar-link" exact-active-class="is-current">Today</router-link>
		<router-link to="/archive" class="topbar-link" active-class="is-current">Archive</router-link>
		<router-link to="/leaderboard" class="topbar-link" active-class="is-current">Leaderboard</router-link>
	</nav>

	<div class="topbar-right">

		<div v-if="signedIn" class="topbar-account-wrap">

			<button
				type="button"
				class="topbar-account"
				aria-label="Account"
				aria-haspopup="true"
				:aria-expanded="menuOpen ? 'true' : 'false'"
				v-on:click="toggleMenu"
			>
				<span class="topbar-avatar" :style="avatarStyle">
					<span v-if="!avatar" class="topbar-initial">{{ initial }}</span>
				</span>
				<span class="topbar-name">{{ name }}</span>
			</button>

			<div v-if="menuOpen" class="topbar-menu" role="menu">
				<span class="topbar-menu-name">{{ name }}</span>
				<button type="button" class="topbar-menu-item" role="menuitem" v-on:click="logout">Sign out</button>
			</div>

		</div>

		<button v-else type="button" class="topbar-signin" v-on:click="signin">Sign in</button>

	</div>

</header>

</template>

<script>

/*
	The one site header, rendered outside the router so it never moves between
	screens: the boxart wordmark left, Today / Archive / Leaderboard nav centre,
	and on the right either a Sign in prompt (guest) or the signed-in account's
	avatar + name. Sign in raises a global `signin` on the bus (App.vue hosts the
	one sign-in dialog) AND emits `signin` for any screen that wants it locally;
	the account chip opens a tiny dropdown with the name and Sign out (there is no
	/account screen).
*/

import bus from '../../service/bus'

export default {

	data: function() {

		return {
			// whether the account dropdown menu is open
			menuOpen: false
		}

	},

	created: function() {

		// close the menu on any outside click
		var self = this

		this.onDocClick = function(e) {

			if (!self.menuOpen) return
			if (self.$el && self.$el.contains(e.target)) return
			self.menuOpen = false

		}

		document.addEventListener('click', this.onDocClick)

	},

	beforeDestroy: function() {

		document.removeEventListener('click', this.onDocClick)

	},

	methods: {

		signin: function() {

			// the dialog is hosted once at the App root; the bus lets any topbar
			// (every screen renders its own) open it without prop-drilling
			bus.$emit('signin')
			this.$emit('signin')

		},

		toggleMenu: function() {

			this.menuOpen = !this.menuOpen

		},

		logout: function() {

			this.menuOpen = false
			this.$store.dispatch('account/logout')

			// back to Today; avoid a redundant navigation error if already there
			if (this.$route.path !== '/') this.$router.push('/').catch(function() {})

		}

	},

	computed: {

		account: function() {

			return this.$store.getters['account/account']

		},

		signedIn: function() {

			return this.$store.getters['account/signedIn']

		},

		name: function() {

			return (this.account && this.account.name) || 'Account'

		},

		avatar: function() {

			return (this.account && this.account.avatar) || null

		},

		avatarStyle: function() {

			return this.avatar ? { backgroundImage: 'url(' + this.avatar + ')' } : {}

		},

		initial: function() {

			return this.name ? this.name.charAt(0).toUpperCase() : '?'

		}

	}

}

</script>

<style scoped>

.topbar {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 18px;
	padding: 16px 20px;
	border-bottom: 1px solid var(--hairline);
}

.topbar-nav {
	display: flex;
	align-items: center;
	gap: 26px;
	margin-left: auto;
}

.topbar-link {
	font-family: var(--font-body);
	font-size: 14px;
	color: var(--muted);
	text-decoration: none;
}

.topbar-link:hover {
	color: var(--ink);
}

.topbar-link.is-current {
	color: var(--ink);
	font-weight: 500;
}

.topbar-right {
	display: flex;
	align-items: center;
}

.topbar-signin {
	font-family: var(--font-body);
	font-size: 14px;
	font-weight: 500;
	color: var(--accent);
	background: none;
	border: none;
	padding: 0 0 0 8px;
	cursor: pointer;
}

.topbar-account-wrap {
	position: relative;
	display: flex;
	align-items: center;
}

.topbar-account {
	display: flex;
	align-items: center;
	gap: 8px;
	padding: 0 0 0 8px;
	text-decoration: none;
	color: var(--ink);
	background: none;
	border: none;
	font: inherit;
	cursor: pointer;
}

.topbar-menu {
	position: absolute;
	top: calc(100% + 8px);
	right: 0;
	min-width: 160px;
	background: var(--paper);
	border: 1px solid var(--hairline);
	border-radius: 10px;
	box-shadow: 0 14px 40px rgba(40, 34, 24, 0.16);
	padding: 6px;
	z-index: 50;
	display: flex;
	flex-direction: column;
}

.topbar-menu-name {
	font-family: var(--font-body);
	font-size: 13px;
	font-weight: 500;
	color: var(--ink);
	padding: 8px 10px 6px;
	border-bottom: 1px solid var(--hairline);
	margin-bottom: 4px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.topbar-menu-item {
	display: block;
	width: 100%;
	text-align: left;
	background: none;
	border: none;
	border-radius: 7px;
	padding: 8px 10px;
	font-family: var(--font-body);
	font-size: 14px;
	color: var(--ink);
	cursor: pointer;
}

.topbar-menu-item:hover {
	background: #f1ebdd;
	color: var(--accent);
}

.topbar-avatar {
	width: 30px;
	height: 30px;
	border-radius: 50%;
	background-color: #ece5d7;
	background-size: cover;
	background-position: center;
	border: 1px solid var(--hairline);
	display: flex;
	align-items: center;
	justify-content: center;
	flex: none;
}

.topbar-initial {
	font-family: var(--font-display);
	font-size: 14px;
	color: var(--muted-2);
}

.topbar-name {
	font-family: var(--font-body);
	font-size: 14px;
	font-weight: 500;
	color: var(--ink);
}

/* phones: keep all three destinations reachable. The wordmark + Sign in /
   account stay on the first row; the nav wraps to its own centred row beneath
   so it never crowds the bar (Archive / Leaderboard are otherwise unreachable
   on mobile). */
@media (max-width: 599px) {

	.topbar {
		flex-wrap: wrap;
		row-gap: 10px;
		padding: 12px 16px;
	}

	.topbar-nav {
		order: 3;
		flex-basis: 100%;
		margin-left: 0;
		justify-content: center;
		gap: 22px;
		padding-top: 10px;
		border-top: 1px solid var(--hairline);
	}

	.topbar-name {
		display: none;
	}

}

</style>
